"use client"

import axios from "axios";
import { useEffect, useRef, useState } from "react";
import { IoIosArrowDown as ArrowDown } from "react-icons/io";
import { NotificationManager } from "react-notifications";
import Select from "react-select";
import dollor from "../../../assets/icons/dollar.svg";
import percentage from "../../../assets/icons/percentage.svg";
import SearchDropdown from "../../../components/Dropdowns/SearchDropdown";
import FilterFieldHeader from "../../../components/FilterComponents/FilterFieldHeader";
import OptionalOption from "../../../components/FilterComponents/OptionalOption";
import InputWithIcon from "../../../components/InputWithIcon";
import useAuth from "../../../hooks/useAuth";
import { BASE_URL } from "../../../utils/Element";
import { ContractToCloseStatusOptions } from "../../../utils/options";

const ContractCloseFilterData = ({
  isSidebarOpen,
  filterData,
  activeTab,
  onSetFilterData,
  onCallApiAgain,
  from
}) => {
  const [config] = useAuth();
  const dropdownRef = useRef(null);
  const [brokerOptions, setBrokerOptions] = useState([]);

  // Separate states for each dropdown to prevent cross-tab conflicts
  const [currentPhaseOptions, setCurrentPhaseOptions] = useState([]);
  const [nextActionOptions, setNextActionOptions] = useState([]);

  const [propertyOptionsTab0, setPropertyOptionsTab0] = useState([]);   // Tab 0
  const [propertyOptionsTab2, setPropertyOptionsTab2] = useState([]);   // Tab 2
  const [propertyOptionsTab3, setPropertyOptionsTab3] = useState([]);   // Tab 3

  const [contactOptions, setContactOptions] = useState([]);              // Key Contact (Tab 0)
  const [acquisitionContactOptions, setAcquisitionContactOptions] = useState([]);
  const [dispositionContactOptions, setDispositionContactOptions] = useState([]);

  const [isStatusOpen, setIsStatusOpen] = useState(false);

  // Fetch Users (Advisor)
  const fetchUsers = () => {
    axios
      .get(`${BASE_URL}/get-datalisting?list=users`, config)
      .then((res) => {
        const data = res?.data?.data || [];
        const options = data.map((el) => ({
          value: el?.id,
          label: `${el?.first_name} ${el?.last_name}`,
          role: el?.role_id,
        }));
        setBrokerOptions(options.filter((el) => el.role === 3));
      })
      .catch((err) => {
        if (err.response?.data?.message && err.response?.data?.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response?.data?.message);
        }
      });
  };

  useEffect(() => {
    if (isSidebarOpen) {
      fetchUsers();
    }
  }, [isSidebarOpen]);

  // Fetch suggestions
  const fetchContractSuggestions = (inputValue, fieldType) => {
    if (!inputValue || inputValue.length < 2) {
      if (fieldType === "current_phase") setCurrentPhaseOptions([]);
      if (fieldType === "next_action") setNextActionOptions([]);
      return;
    }

    axios
      .get(`${BASE_URL}/get-datalisting?list=contract_to_close&search=${inputValue}&field=${fieldType}`, config)
      .then((res) => {
        const data = res?.data?.contract_to_close || {};
        const options = Object.entries(data).map(([_, label]) => ({
          value: label,
          label: label,
        }));

        if (fieldType === "current_phase") setCurrentPhaseOptions(options);
        else if (fieldType === "next_action") setNextActionOptions(options);
      })
      .catch((err) => {
        console.error("Suggestions Error:", err);
        if (err.response?.data?.message && err.response?.data?.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response?.data?.message);
        }
      });
  };

  const fetchPropertySuggestions = (inputValue, setter) => {
    axios
      .get(`${BASE_URL}/get-datalisting?list=properties&search=${inputValue}`, config)
      .then((res) => {
        const data = res.data.data.properties || [];
        const options = data.map((el) => ({
          value: el.id,
          label: el?.property_name,
          desc: `${el.street_address}, ${el.city}, ${el.state}`,
        }));
        setter(options);
      })
      .catch((err) => {
        if (err.response?.data?.message && err.response?.data?.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response?.data?.message);
        }
      });
  };

  const fetchContactSuggestions = (inputValue, setter) => {
    axios
      .get(`${BASE_URL}/get-datalisting?list=contacts&search=${inputValue}`, config)
      .then((res) => {
        const data = res.data.data.contact_list || [];
        const options = data.map((contact) => ({
          value: contact.id,
          label: `${contact.first_name} ${contact.last_name}`,
        }));
        setter(options);
      })
      .catch((err) => {
        if (err.response?.data?.message && err.response?.data?.message !== "Too Many Attempts.") {
          NotificationManager.error(err.response?.data?.message);
        }
      });
  };

  const handleFilterChange = (value, name) => {
    onSetFilterData((prev) => ({ ...prev, [name]: value }));
  };

  // Archive mode
  if (from === "contractclosearchive") {
    return (
      <div>
        <p className="head-4 dark-H">Date Range</p>
        <OptionalOption
          activeTab="date"
          onSetLastDate={(value) => onSetFilterData((prev) => ({ ...prev, count: value }))}
          date={filterData?.count}
          onSetDateRange1={(value) => onSetFilterData((prev) => ({ ...prev, start_count: value }))}
          date2={filterData?.start_count}
          onSetDateRange2={(value) => onSetFilterData((prev) => ({ ...prev, end_count: value }))}
          date3={filterData?.end_count}
          onClear={() => {
            onSetFilterData((prev) => ({
              ...prev,
              count: "",
              start_count: "",
              end_count: "",
            }));
          }}
        />
      </div>
    );
  }

  return (
    <>
      {activeTab === 0 && (
        <>
          {/* Current Phase */}
          <div className="mt-4">
            <label className="dark-H head-4 mb-2">Current Phase</label>
            <SearchDropdown
              className="body-N"
              placeholder="Search Current Phase..."
              text={filterData?.current_phase || ""}
              options={currentPhaseOptions}
              fetchSuggestions={(inputValue) => fetchContractSuggestions(inputValue, "current_phase")}
              onSetOptions={setCurrentPhaseOptions}
              handleSelect={(option) => {
                handleFilterChange(option?.label || option?.value, "current_phase");
                setCurrentPhaseOptions([]);
              }}
            />
          </div>

          <hr className="my-6" style={{ borderColor: "#6F6F6F" }} />

          {/* Next Action */}
          <div className="mt-4">
            <label className="dark-H head-4 mb-2">Next Action</label>
            <SearchDropdown
              className="body-N"
              placeholder="Search Next Action..."
              text={filterData?.next_action || ""}
              options={nextActionOptions}
              fetchSuggestions={(inputValue) => fetchContractSuggestions(inputValue, "next_action")}
              onSetOptions={setNextActionOptions}
              handleSelect={(option) => {
                handleFilterChange(option?.label || option?.value, "next_action");
                setNextActionOptions([]);
              }}
            />
          </div>

          <hr className="my-6" style={{ borderColor: "#6F6F6F" }} />

          {/* Property - Tab 0 */}
          <div className="mt-4">
            <label className="dark-H head-4 mb-4">Property</label>
            <SearchDropdown
              isTop={false}
              placeholder="Search Property"
              text={filterData?.property?.name || ""}
              options={propertyOptionsTab0}
              fetchSuggestions={(inputValue) => fetchPropertySuggestions(inputValue, setPropertyOptionsTab0)}
              onSetOptions={setPropertyOptionsTab0}
              handleSelect={(option) => {
                handleFilterChange({ id: option?.value, name: option?.label }, "property");
                setPropertyOptionsTab0([]);
              }}
            />
          </div>

          <hr className="my-6" style={{ borderColor: "#6F6F6F" }} />

          {/* Key Contact */}
          <div>
            <p className="head-4 dark-H">Key Contact</p>
            <SearchDropdown
              isTop={false}
              placeholder="Search contact"
              text={filterData?.contact?.name || ""}
              options={contactOptions}
              fetchSuggestions={(input) => fetchContactSuggestions(input, setContactOptions)}
              onSetOptions={setContactOptions}
              handleSelect={(option) => {
                handleFilterChange({ id: option?.value, name: option?.label }, "contact");
                setContactOptions([]);
              }}
            />
          </div>
        </>
      )}

      {activeTab === 2 && (
        <>
          {/* Property - Tab 2 */}
          <div className="mt-4">
            <label className="dark-H head-4 mb-4">Property Name</label>
            <SearchDropdown
              isTop={false}
              placeholder="Search Property"
              text={filterData?.property?.name || ""}
              options={propertyOptionsTab2}
              fetchSuggestions={(inputValue) => fetchPropertySuggestions(inputValue, setPropertyOptionsTab2)}
              onSetOptions={setPropertyOptionsTab2}
              handleSelect={(option) => {
                handleFilterChange({ id: option?.value, name: option?.label }, "property");
                setPropertyOptionsTab2([]);
              }}
            />
          </div>

          <hr className="my-6" style={{ borderColor: "#6F6F6F" }} />

          {/* Advisor */}
          <div>
            <p className="head-4 dark-H">Advisor</p>
            <div className="w-[100%] mt-2">
              <Select
                className="body-S"
                placeholder="Search here"
                value={brokerOptions?.find((el) => el?.value == filterData?.advisor) || null}
                options={brokerOptions}
                onChange={(option) => handleFilterChange(option?.value, "advisor")}
              />
            </div>
          </div>

          <hr className="my-6" style={{ borderColor: "#6F6F6F" }} />

          {/* Status */}
          <div className="mt-4">
            <p className="head-4 dark-H">Status</p>
            <div ref={dropdownRef} className="custom-dropdown mt-2">
              <div
                role="button"
                className="select-header-input capitalize light-bg-L body-N dark-M flex justify-between items-center"
                onClick={() => setIsStatusOpen(!isStatusOpen)}
              >
                {filterData.status === "" ? "Select" : ContractToCloseStatusOptions.find((el) => el.value === filterData.status)?.label || "Select"}
                <ArrowDown />
              </div>
              {isStatusOpen && (
                <div className="dropdown-list-container light-bg-L dark-M body-N shadow rounded-box" style={{ width: "100%" }}>
                  <ul className="dropdown-list">
                    {ContractToCloseStatusOptions.map((el) => (
                      <li
                        key={el.value}
                        role="button"
                        onClick={() => {
                          handleFilterChange(el.value, "status");
                          setIsStatusOpen(false);
                        }}
                        className={filterData.status === el.value ? "active" : ""}
                      >
                        {el.label}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>

          <hr className="my-6" style={{ borderColor: "#6F6F6F" }} />

          {/* Acquisition */}
          <div>
            <p className="head-4 dark-H">Acquisition</p>
            <SearchDropdown
              isTop={false}
              placeholder="Search Acquisition Contact"
              text={filterData?.acquisition_contact?.name || ""}
              options={acquisitionContactOptions}
              fetchSuggestions={(input) => fetchContactSuggestions(input, setAcquisitionContactOptions)}
              onSetOptions={setAcquisitionContactOptions}
              handleSelect={(option) => {
                handleFilterChange({ id: option?.value, name: option?.label }, "acquisition_contact");
                setAcquisitionContactOptions([]);
              }}
            />
          </div>

          <hr className="my-6" style={{ borderColor: "#6F6F6F" }} />

          {/* Disposition */}
          <div>
            <p className="head-4 dark-H">Disposition</p>
            <SearchDropdown
              isTop={false}
              placeholder="Search Disposition Contact"
              text={filterData?.disposition_contact?.name || ""}
              options={dispositionContactOptions}
              fetchSuggestions={(input) => fetchContactSuggestions(input, setDispositionContactOptions)}
              onSetOptions={setDispositionContactOptions}
              handleSelect={(option) => {
                handleFilterChange({ id: option?.value, name: option?.label }, "disposition_contact");
                setDispositionContactOptions([]);
              }}
            />
          </div>

          <hr className="my-6" style={{ borderColor: "#6F6F6F" }} />

          {/* Executed LOI Date */}
          <div>
            <p className="head-4 dark-H capitalize">Executed LOI Date</p>
            <OptionalOption
              activeTab="date"
              onSetLastDate={(value) => handleFilterChange(value, "created_at")}
              date={filterData?.created_at}
              onSetDateRange1={(value) => handleFilterChange(value, "created_start_date")}
              date2={filterData?.created_start_date}
              onSetDateRange2={(value) => handleFilterChange(value, "created_end_date")}
              date3={filterData?.created_end_date}
              onClear={() => {
                onSetFilterData((prev) => ({
                  ...prev,
                  created_at: "",
                  created_start_date: "",
                  created_end_date: "",
                }));
              }}
            />
          </div>

          <hr className="my-6" style={{ borderColor: "#6F6F6F" }} />

          {/* Under Contract Date */}
          <div>
            <p className="head-4 dark-H capitalize">Under Contract Date</p>
            <OptionalOption
              activeTab="date"
              onSetLastDate={(value) => handleFilterChange(value, "under_contract_created_at")}
              date={filterData?.under_contract_created_at}
              onSetDateRange1={(value) => handleFilterChange(value, "under_contract_created_start_date")}
              date2={filterData?.under_contract_created_start_date}
              onSetDateRange2={(value) => handleFilterChange(value, "under_contract_created_end_date")}
              date3={filterData?.under_contract_created_end_date}
              onClear={() => {
                onSetFilterData((prev) => ({
                  ...prev,
                  under_contract_created_at: "",
                  under_contract_created_start_date: "",
                  under_contract_created_end_date: "",
                }));
              }}
            />
          </div>

          <hr className="my-6" style={{ borderColor: "#6F6F6F" }} />

          {/* Closed / Expected Close Date */}
          <div>
            <p className="head-4 dark-H capitalize">Closed / Expected Close Date</p>
            <OptionalOption
              activeTab="date"
              onSetLastDate={(value) => handleFilterChange(value, "closed_expected_created_at")}
              date={filterData?.closed_expected_created_at}
              onSetDateRange1={(value) => handleFilterChange(value, "closed_expected_created_start_date")}
              date2={filterData?.closed_expected_created_start_date}
              onSetDateRange2={(value) => handleFilterChange(value, "closed_expected_created_end_date")}
              date3={filterData?.closed_expected_created_end_date}
              onClear={() => {
                onSetFilterData((prev) => ({
                  ...prev,
                  closed_expected_created_at: "",
                  closed_expected_created_start_date: "",
                  closed_expected_created_end_date: "",
                }));
              }}
            />
          </div>
        </>
      )}

      {activeTab === 3 && (
        <>
          {/* Property - Tab 3 */}
          <div className="mt-4">
            <label className="dark-H head-4 mb-4">Property Name</label>
            <SearchDropdown
              isTop={false}
              placeholder="Search Property"
              text={filterData?.property?.name || ""}
              options={propertyOptionsTab3}
              fetchSuggestions={(inputValue) => fetchPropertySuggestions(inputValue, setPropertyOptionsTab3)}
              onSetOptions={setPropertyOptionsTab3}
              handleSelect={(option) => {
                handleFilterChange({ id: option?.value, name: option?.label }, "property");
                setPropertyOptionsTab3([]);
              }}
            />
          </div>

          <hr className="my-6" style={{ borderColor: "#6F6F6F" }} />

          {/* Advisor */}
          <div>
            <p className="head-4 dark-H">Advisor</p>
            <div className="w-[100%] mt-2">
              <Select
                className="body-S"
                placeholder="Search here"
                value={brokerOptions?.find((el) => el?.value == filterData?.advisor) || null}
                options={brokerOptions}
                onChange={(option) => handleFilterChange(option?.value, "advisor")}
              />
            </div>
          </div>

          <hr className="my-6" style={{ borderColor: "#6F6F6F" }} />

          {/* Lease Type */}
          <div>
            <p className="head-4 dark-H">Lease Type</p>
            <OptionalOption
              activeTab="date"
              type="number"
              title="Range"
              placeholder="enter here"
              onSetLastDate={(value) => handleFilterChange(value, "lease_type_count")}
              date={filterData?.lease_type_count || ""}
              onSetDateRange1={(value) => handleFilterChange(value, "lease_type_start_count")}
              date2={filterData?.lease_type_start_count || ""}
              onSetDateRange2={(value) => handleFilterChange(value, "lease_type_end_count")}
              date3={filterData?.lease_type_end_count || ""}
              onClear={() => {
                onSetFilterData((prev) => ({
                  ...prev,
                  lease_type_count: "",
                  lease_type_start_count: "",
                  lease_type_end_count: "",
                }));
              }}
            />
          </div>

          <hr className="my-6" style={{ borderColor: "#6F6F6F" }} />

          {/* Lease Term Remaining */}
          <div>
            <p className="head-4 dark-H">Lease Term Remaining</p>
            <OptionalOption
              activeTab="date"
              type="number"
              title="Range"
              placeholder="enter here"
              onSetLastDate={(value) => handleFilterChange(value, "count")}
              date={filterData?.count || ""}
              onSetDateRange1={(value) => handleFilterChange(value, "start_count")}
              date2={filterData?.start_count || ""}
              onSetDateRange2={(value) => handleFilterChange(value, "end_count")}
              date3={filterData?.end_count || ""}
              onClear={() => {
                onSetFilterData((prev) => ({
                  ...prev,
                  count: "",
                  start_count: "",
                  end_count: "",
                }));
              }}
            />
          </div>

          <hr className="my-6" style={{ borderColor: "#6F6F6F" }} />

          {/* NOI */}
          <div>
            <p className="head-4 dark-H">NOI</p>
            <div className="mt-2">
              <InputWithIcon
                icon={dollor}
                type="number"
                min={0}
                placeholder="write here"
                value={filterData?.noi || ""}
                onChange={(e) => handleFilterChange(e.target.value, "noi")}
              />
            </div>
          </div>

          <hr className="my-6" style={{ borderColor: "#6F6F6F" }} />

          {/* Contract Price */}
          <div>
            <FilterFieldHeader
              title={"Contract price"}
              data={filterData?.min_contract_price}
              type="to"
              symbol={"  to  "}
              data2={filterData?.max_contract_price}
              handleCross={() => {
                onSetFilterData((prev) => ({
                  ...prev,
                  min_contract_price: "",
                  max_contract_price: "",
                }));
              }}
            />
            <div className="flex flex-row mt-2 gap-3">
              <div className="w-[50%]">
                <InputWithIcon
                  icon={percentage}
                  type="number"
                  min={0}
                  placeholder="min rate"
                  value={filterData?.min_contract_price || ""}
                  onChange={(e) => handleFilterChange(e.target.value, "min_contract_price")}
                />
              </div>
              <div className="w-[50%]">
                <InputWithIcon
                  icon={percentage}
                  type="number"
                  min={0}
                  placeholder="max rate"
                  value={filterData?.max_contract_price || ""}
                  onChange={(e) => handleFilterChange(e.target.value, "max_contract_price")}
                />
              </div>
            </div>
          </div>

          <hr className="my-6" style={{ borderColor: "#6F6F6F" }} />

          {/* Contract Cap Rate */}
          <div>
            <FilterFieldHeader
              title={"Contract Cap Rate"}
              data={filterData?.min_contract_cap_rate}
              type="to"
              symbol={"  to  "}
              data2={filterData?.max_contract_cap_rate}
              handleCross={() => {
                onSetFilterData((prev) => ({
                  ...prev,
                  min_contract_cap_rate: "",
                  max_contract_cap_rate: "",
                }));
              }}
            />
            <div className="flex flex-row mt-2 gap-3">
              <div className="w-[50%]">
                <InputWithIcon
                  icon={percentage}
                  type="number"
                  min={0}
                  placeholder="min rate"
                  value={filterData?.min_contract_cap_rate || ""}
                  onChange={(e) => handleFilterChange(e.target.value, "min_contract_cap_rate")}
                />
              </div>
              <div className="w-[50%]">
                <InputWithIcon
                  icon={percentage}
                  type="number"
                  min={0}
                  placeholder="max rate"
                  value={filterData?.max_contract_cap_rate || ""}
                  onChange={(e) => handleFilterChange(e.target.value, "max_contract_cap_rate")}
                />
              </div>
            </div>
          </div>

          <hr className="my-6" style={{ borderColor: "#6F6F6F" }} />

          {/* % of Asking Price */}
          <div>
            <FilterFieldHeader
              title={"% of Asking Price"}
              data={filterData?.min_asking_price}
              type="to"
              symbol={"  to  "}
              data2={filterData?.max_asking_price}
              handleCross={() => {
                onSetFilterData((prev) => ({
                  ...prev,
                  min_asking_price: "",
                  max_asking_price: "",
                }));
              }}
            />
            <div className="flex flex-row mt-2 gap-3">
              <div className="w-[50%]">
                <InputWithIcon
                  icon={percentage}
                  type="number"
                  min={0}
                  placeholder="min rate"
                  value={filterData?.min_asking_price || ""}
                  onChange={(e) => handleFilterChange(e.target.value, "min_asking_price")}
                />
              </div>
              <div className="w-[50%]">
                <InputWithIcon
                  icon={percentage}
                  type="number"
                  min={0}
                  placeholder="max rate"
                  value={filterData?.max_asking_price || ""}
                  onChange={(e) => handleFilterChange(e.target.value, "max_asking_price")}
                />
              </div>
            </div>
          </div>
        </>
      )}
    </>
  );
};

export default ContractCloseFilterData;